import type { ComposeDraft } from "@/lib/types/compose";
import type { ComposePlacement } from "@/lib/compose/composePlacement";
import { migrateComposeDraftBody } from "@/lib/compose/composeHtml";
import { stripAttachmentsForStorage } from "@/lib/compose/composeAttachments";

const STORAGE_KEY = "aurelia-mail-compose-sessions:v1";

export type StoredComposeSession = {
  id: string;
  accountId: number;
  draft: ComposeDraft;
  createdAt: number;
  updatedAt: number;
  /** Réponse inline dans le lecteur (desktop) ; absent ou modal = drawer. */
  placement?: ComposePlacement;
  /** Message cité affiché sous la zone de rédaction. */
  inlineReplySourceUid?: number;
};

type StoreShape = Record<string, StoredComposeSession[]>;

function readStore(): StoreShape {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as StoreShape;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function writeStore(store: StoreShape): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
}

export function readComposeSessions(accountId: number): StoredComposeSession[] {
  const list = readStore()[String(accountId)] ?? [];
  return list
    .filter((s) => s && typeof s.id === "string" && s.draft)
    .map((s) => {
      const body = migrateComposeDraftBody(s.draft);
      return {
        ...s,
        draft: {
          ...s.draft,
          bodyText: body.bodyText,
          bodyHtml: body.bodyHtml,
        },
      };
    });
}

export function writeComposeSessions(accountId: number, sessions: StoredComposeSession[]): void {
  const store = readStore();
  const sanitized = sessions.map((session) => ({
    ...session,
    draft: stripAttachmentsForStorage(session.draft),
  }));
  if (sanitized.length === 0) {
    delete store[String(accountId)];
  } else {
    store[String(accountId)] = sanitized;
  }
  writeStore(store);
}

export function countComposeSessions(accountId: number): number {
  return readComposeSessions(accountId).length;
}

export function newComposeSessionId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `compose-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function sessionTabLabel(draft: ComposeDraft): string {
  const subject = draft.subject.trim();
  if (subject) return subject;
  if (draft.to.trim()) return `À ${draft.to.trim()}`;
  return "Nouveau message";
}
